/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useState } from "react";
import { Link } from "react-router-dom";
import { DialogTitle } from "@headlessui/react";
import { TrashIcon } from "@heroicons/react/24/outline";
import Drawer from "../../../blocks/drawer";
import { useLanguage } from "../../../contexts/LanguageContext";
import { formatInZone } from "../../../utils/zonedTime";
import { timeZoneLabel } from "../../../utils/timeZones";
import {
  WHEN_FORMAT,
  audienceLabel,
  channelName,
  failureCopy,
  messageSendTime,
  recipientsText,
} from "../messageUtils";
import { buttonClass } from "../styles";
import { StatusPill } from "./MessageTable";
import { CloseButton } from "./ui";

const WHEN_LABEL_KEYS = {
  pending: "marketing.view.scheduledFor",
  sent: "marketing.view.sentAt",
};

// Why a failed message failed and what the merchant can do about it. The table
// shows only the hint; this is the full text.
function FailureNote({ message, t }) {
  const copy = failureCopy(message, t);
  if (!copy) return null;
  return (
    <div className="flex flex-col gap-1 rounded-[10px] bg-red-50 px-3.5 py-3 text-[13px] leading-normal text-red-900">
      {copy.hint && <p className="m-0 font-semibold">{copy.hint}</p>}
      <p className="m-0">{copy.detail}</p>
      {copy.reason === "provider_rejected" ? (
        <Link
          to={`/integrations?tag=${message.message_type}`}
          className="self-start font-semibold text-red-700 underline underline-offset-2 hover:text-red-800"
        >
          {copy.action}
        </Link>
      ) : (
        copy.action && <p className="m-0">{copy.action}</p>
      )}
    </div>
  );
}

// Read-only view of one message. Edit is offered while it is still pending;
// Delete for any status. Both only for those who can manage the store.
export default function MessageDetailDrawer({
  message,
  timeZone,
  onClose,
  onEdit,
  onDelete,
}) {
  const { t, language } = useLanguage();
  const open = !!message;
  // Keep showing the last message while the drawer slides closed.
  const [shown, setShown] = useState(message);
  if (message && message !== shown) setShown(message);
  const m = message || shown || {};
  const when = formatInZone(
    messageSendTime(m),
    timeZone,
    language,
    WHEN_FORMAT,
  );

  const rows = [
    [t("marketing.table.audience"), audienceLabel(m, t)],
    [
      t(WHEN_LABEL_KEYS[m.status] || "marketing.table.when"),
      when ? `${when} (${timeZoneLabel(timeZone)})` : "—",
    ],
    [t("marketing.table.recipients"), recipientsText(m, t, language)],
    [t("marketing.view.channel"), channelName(m.message_type, t)],
    [
      t("marketing.form.from"),
      m.message_type === "push"
        ? t("marketing.view.fromWallet")
        : m.from || "—",
    ],
  ];

  return (
    <Drawer
      open={open}
      setOpen={onClose}
      size="full"
      panelClassName="sm:max-w-[480px]"
      headerClassName="bg-white text-gray-900"
      showCloseButton={false}
      showFooter={false}
      header={
        <>
          <DialogTitle as="h2" className="text-xl font-semibold text-gray-900">
            {channelName(m.message_type, t)}
          </DialogTitle>
          <CloseButton onClick={onClose} label={t("common.close")} />
        </>
      }
    >
      {shown && (
        <div className="flex min-h-full flex-col gap-5 px-6 pt-1 pb-6">
          <div className="flex flex-col gap-2">
            <span className="self-start">
              <StatusPill status={m.status} />
            </span>
            <FailureNote message={m} t={t} />
          </div>
          {m.message_type === "email" && m.subject && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-gray-600">
                {t("marketing.form.emailSubject")}
              </span>
              <span className="text-[15px] font-semibold text-gray-900">
                {m.subject}
              </span>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-600">
              {t("marketing.form.message")}
            </span>
            {m.message_type === "email" ? (
              <div
                className="prose prose-sm max-w-none rounded-[10px] bg-gray-50 px-3.5 py-3"
                dangerouslySetInnerHTML={{ __html: m.message }}
              />
            ) : (
              <p className="m-0 rounded-[10px] bg-gray-50 px-3.5 py-3 text-sm leading-relaxed whitespace-pre-wrap text-gray-900">
                {m.message}
              </p>
            )}
          </div>
          <dl className="m-0 grid grid-cols-[120px_minmax(0,1fr)] gap-x-4 gap-y-3 text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-gray-600">{label}</dt>
                <dd className="m-0 text-gray-900 tabular-nums [overflow-wrap:anywhere]">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          <div className="mt-auto flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4">
            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete(m)}
                className={`${buttonClass.dangerOutline} mr-auto`}
              >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
                {t("marketing.delete.confirm")}
              </button>
            )}
            {onEdit && m.status === "pending" ? (
              <button
                type="button"
                onClick={() => onEdit(m)}
                className={buttonClass.primary}
              >
                {t("marketing.table.edit")}
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className={buttonClass.secondary}
              >
                {t("common.close")}
              </button>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
